import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DateInputFillMode, DateInputRounded, DateInputSize } from '@progress/kendo-angular-dateinputs';
import { CheckBoxRounded, InputSize } from '@progress/kendo-angular-inputs';
import { ENV_CONFIG } from 'src/app/core/injection.token';
import { NotificationService } from 'src/app/core/services/notification.service';
import { USER_ROLES } from 'src/app/extensions/constants/commonConstants';
import { EMAIL_VALIDATOR_REGEX } from 'src/app/extensions/constants/form-validator.constants';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { TenantExceptions } from 'src/app/extensions/services/api/exceptions';
import { TenantAPIService } from 'src/app/extensions/services/api/tenant-api.service';
import { UserAPIService } from 'src/app/extensions/services/api/user-api.service';

@Component({
  selector: 'app-admin-user-detail',
  templateUrl: './admin-user-detail.component.html',
  styleUrls: ['./admin-user-detail.component.scss'],
  standalone: false,
})
export class AdminUserDetailComponent implements OnInit {
  @ViewChild('form') public form: NgForm;

  public size: InputSize = 'large';
  public rounded: CheckBoxRounded = 'large';

  user: app.models.UserModel = {
    isAdmin: true,
    acceptEmails: false,
    acceptSms: false,
    legalRepresentative: true,
    dictionaryTwoFactorAuthenticationTypeId: null,
  } as app.models.UserModel;

  loading: boolean = false;
  localeValidators: app.models.Validator[] = [];
  phoneValidator: app.models.Validator;
  externalProviders: app.models.DictionaryDataModel[] = [];
  isDisableExternalProviders: boolean = true;
  isEmailReadOnly: boolean = true;
  tenantConfiguration = { twoFactorAuthentication: false };
  emailValidator = EMAIL_VALIDATOR_REGEX;
  twoFactorAutenticationTypes: app.models.DictionaryDataModel[];
  enableLocalUser: boolean;

  public fillModeOutline: DateInputFillMode = 'outline';
  public roundedLarge: DateInputRounded = 'large';
  public sizeLarge: DateInputSize = 'large';

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _userAPIService: UserAPIService,
    private _router: Router,
    private _notificationService: NotificationService,
    private _translateService: TranslateService,
    private _tenantAPIService: TenantAPIService,
    private _dictionaryAPIService: DictionaryAPIService,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvConfigurationData
  ) {
    this.enableLocalUser = JSON.parse(this._envConfig.ENABLE_LOCAL_USER || 'true');
  }

  async ngOnInit(): Promise<void> {
    this.user.userId = this._activatedRoute.snapshot.params['userId'];
    if (!this.user.userId) {
      this.isDisableExternalProviders = false;
      this.isEmailReadOnly = false;
    }

    this.loadData();
    this.setValidationRule();
  }

  async setValidationRule() {
    this.localeValidators = await this._tenantAPIService.getLocaleValidators().toPromise();
    this.phoneValidator = this.localeValidators.find(x => x.code == 'MOBILE_PHONE');
  }

  loadData(): void {
    if (this.user.userId) {
      this._userAPIService.getTenantUser('', this.user.userId).subscribe(user => {
        this.user = { ...user, isAdmin: true, legalRepresentative: true };
        if (user.externalProviderId) {
          this.user.hasExternalProvider = true;
        } else {
          this.isDisableExternalProviders = false;
        }
      });
    }

    this._tenantAPIService.hasTenantTwoFactorAuthentication().subscribe(twoFactorAuth => {
      this.tenantConfiguration.twoFactorAuthentication = twoFactorAuth;
    });

    this._tenantAPIService.getExternalProviders(USER_ROLES.TENANT_ADMIN).subscribe(externalProviders => {
      this.externalProviders = externalProviders;
      if (!this.enableLocalUser && !this.user.hasExternalProvider) {
        this.user.hasExternalProvider = true;
        this.hasExternalProviderChange(true);
      }
    });

    this._dictionaryAPIService.getDictionaryTwoFactorAuthenticationTypes().subscribe(twoFactorTypes => {
      this.twoFactorAutenticationTypes = twoFactorTypes;
    });
  }

  saveUserHandler() {
    if (!this.form.valid) {
      return;
    }

    this.loading = true;
    this._userAPIService.saveUser(this.user).subscribe(response => {
      if (response.isSuccess) {
        this._notificationService.showSuccess(
          this._translateService.instant('users.add.notifications.userSaved'),
          this._translateService.instant('common.success')
        );
        this.form.resetForm();
        this.redirectUsers();
      } else {
        if (response.response.events.find(x => x.eventBody.exMessage == TenantExceptions.EmailAlreadyExists)) {
          this._notificationService.showWarning(
            this._translateService.instant('users.add.notifications.emailAlreadyExists'),
            this._translateService.instant('common.warning')
          );
        } else if (response.response.events.find(x => x.eventBody.exMessage == TenantExceptions.UserHasRoleCustomer)) {
          this._notificationService.showWarning(
            this._translateService.instant('users.add.notifications.userHasRoleCustomer'),
            this._translateService.instant('common.warning')
          );
        } else {
          this._notificationService.showError(
            this._translateService.instant('users.add.notifications.userSaveFailed'),
            this._translateService.instant('common.error')
          );
        }
      }
      this.loading = false;
    });
  }

  redirectUsers() {
    this._router.navigate(['adminUsers']);
  }

  previousExternalProvider: string = '';
  externalProviderChange(ev: any) {
    if (!this.enableLocalUser && this.previousExternalProvider === ev.currentTarget.id) {
      this.user.externalProviderId = this.previousExternalProvider;
    } else if (this.previousExternalProvider === ev.currentTarget.id) {
      this.user.externalProviderId = '';
    }
    this.previousExternalProvider = ev.currentTarget.id;
  }

  hasExternalProviderChange(hasExternalProvider: boolean) {
    if (hasExternalProvider) {
      this.user.externalProviderId = this.externalProviders[0].id;
    } else {
      this.user.externalProviderId = '';
    }
  }
}
